import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { FiActivity } from 'react-icons/fi';
import { formatTimeAgo } from '@core/utils/formatTimeAgo';
import type { DashboardRecentActivity } from '../types/dashboard';
import { DASHBOARD_LAYOUT } from '../constants/dashboardLayout';

interface Props {
  items: DashboardRecentActivity[];
}

export const RECENT_ACTIVITY_EMPTY_TEXT = 'No recent activity yet.';

export function activityInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
}

const RecentActivityFeed: React.FC<Props> = ({ items }) => {
  const [atEnd, setAtEnd] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setAtEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
  };

  return (
    <Card className="ntcog-card dashboard-section-card dashboard-activity-card">
      <Card.Header>Recent activity</Card.Header>
      <Card.Body className="p-0">
        {!items.length ? (
          <p className="dashboard-activity-empty text-muted mb-0">{RECENT_ACTIVITY_EMPTY_TEXT}</p>
        ) : (
          <div
            className={`dashboard-activity-scroll${atEnd ? ' dashboard-activity-scroll--end' : ''}`}
            tabIndex={0}
            role="region"
            aria-label="Recent activity"
            onScroll={handleScroll}
            style={
              {
                '--dashboard-activity-rows': DASHBOARD_LAYOUT.RECENT_ACTIVITY_VISIBLE_ROWS,
              } as React.CSSProperties
            }
          >
            <ul className="list-unstyled mb-0">
              {items.map((item) => (
                <li key={item.activity_id} className="dashboard-activity-row">
                  <div className="dashboard-activity-avatar" aria-hidden="true">
                    {activityInitials(item.display_name) || <FiActivity size={14} />}
                  </div>
                  <div className="min-w-0 flex-grow-1">
                    <div className="fw-medium">{item.display_name}</div>
                    <small className="text-muted d-block dashboard-activity-row__desc">
                      {item.description}
                    </small>
                  </div>
                  <small className="text-muted flex-shrink-0">{formatTimeAgo(item.occurred_at)}</small>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default RecentActivityFeed;
